import SwivuModal from "../../../react/components/editors/SwivuModal.jsx";
import { useCallback } from "react";
import { ObjectHelper, Store } from "@bryntum/schedulerpro";
import EditorBase from "../base/EditorBase.jsx";
import { classNameHelper, lineThroughString } from "../../../other/utils/utils.jsx";

class SwivuEntityTagEditor extends EditorBase {
  static get $name() {
    return "SwivuEntityTagEditor";
  }

  static get type() {
    return "swivuentitytageditor";
  }

  static get configurable() {
    return {
      autoClose: true,
      closable: true,
      floating: true,
      centered: false,
      draggable: false,
      minWidth: "18em",
      minHeight: "15em",
      width: "18em",
      height: "auto",
      maxWidth: "40em",
      maxHeight: "30em",
      header: {
        title: "",
        cls: "pt-[1em] pb-0 bg-transparent dark:text-[#a7a7a9] text-[#000]"
      },
      contentElementCls: "flex-col flex-nowrap gap-[1em]",
      tbar: {
        cls: "swiviu-finder-toolbar",
        hidden: true,
        items: {
          searchField: {
            type: "text",
            placeholder: "",
            onInput: "up.onSearch",
            onClear: "up.onSearchClear",
            clearable: true,
            flex: 1,
            cls: "swivu-entity-tag-search",
            triggers: {
              searchIcon: {
                cls: 'b-fa b-fa-search pt-[1px]',
                align: "start",
              },
              escButton: {
                // text to this element is assigned from loadRecord/editRecord below
                cls: "text-[0.9em]",
                align: "end",
                handler: "up.close"
              }
            }
          },
        }
      },
      items: {
        tagsList: {
          type: "list",
          scrollable: true,
          multiSelect: false,
          cls: "swivu-tag-list",
          flex: "1 1 0%",
          onItem: "up.onClickItem",
        },
        emptyText: {
          type: "displayfield",
          html: "No data yet.",
          flex: "1 1 0%",
          hidden: true
        },
        manageBtn: {
          type: "button",
          text: "Manage...",
          onClick: "up.onClickManageBtn",
          cls: "w-full dark:text-[#a7a7a9] text-[#000] bg-transparent dark:hover:bg-[#4b4b4e] hover:bg-[#d9d9d9]",
          hidden: true
        }
      }
    };
  }

  editRecord(record, context, otherParams) {
    super.editRecord(record, context, otherParams);
    this.record = record;
    Object.assign(this, context, otherParams);
    if (otherParams.isFirstShow) this.applyOrderSortToList();
    const { column } = otherParams;
    column.swivuRecord = record;
    this.swivuColumn = column;
    const { tagsList, emptyText, close, manageBtn, searchField, tbar } = this.widgetMap;
    this.lastSelection = [];
    const {
      canMultiSelectSwivuList = false,
      swivuListTitle,
      getSelectedSwivuListItem,
      onSwivuManageList,
      swivuListItems,
      getSwivuListItemDisplayValue,
      swivuListSearchKey,
      popupCls = []
    } = this.swivuColumn;
    if (popupCls.length) this.element.classList.add(...popupCls)
    this.title = swivuListTitle;
    tbar.hidden = !swivuListSearchKey;
    if (swivuListSearchKey) {
      searchField.placeholder = swivuListTitle;
      this.widgetMap.searchField.triggers.escButton.element.innerText = "ESC";
      searchField.focus();
      // Bug: Hiding header element via styles closes the editor
      this.autoClose = false;
      this.headerElement.style.display = "none";
      // If delay is not applied, then editor still hides it's itself
      setTimeout(() => {
        this.autoClose = true;
      }, 150)
    }
    const selectedItem = getSelectedSwivuListItem.call(this.swivuColumn, record);
    close.element.classList.remove("b-icon", "b-popup-close");
    close.element.innerText = "X";
    manageBtn.hidden = !onSwivuManageList;
    if (swivuListItems.length) {
      tagsList.hidden = false;
      emptyText.hidden = true;
      tagsList.multiSelect = canMultiSelectSwivuList;
      tagsList.itemTpl = (item) => `<span class="${classNameHelper(item.disabled && "text-gray-300 dark:text-gray-500 pointer-events-none")}">${lineThroughString(getSwivuListItemDisplayValue.call(this.swivuColumn, item), item.archived)}</span>`;
      tagsList.items = swivuListItems;
      if (selectedItem) {
        tagsList.select(selectedItem);
      }
      this.lastSelection = ObjectHelper.clone(tagsList.selected.values);
    } else {
      tagsList.hidden = true;
      emptyText.hidden = false;
    }
  }

  onClickItem({ source, record }) {
    if (record.disabled) {
      source.deselectAll();
      source.select(this.swivuColumn.getSelectedSwivuListItem.call(this.swivuColumn, this.record));
      return;
    };
    const selected = source.selected.values;
    const {
      getSelectedSwivuListItem,
      canEmptySwivuList = true,
      onSwivuListSelectionChange,
      canMultiSelectSwivuList,
      onSwivuBeforeListSelectionChange = () => true
    } = this.swivuColumn;
    const previousSelection = getSelectedSwivuListItem.call(this.swivuColumn, this.record);
    if (onSwivuBeforeListSelectionChange.call(this.swivuColumn, this.record, selected)) {
      const action = this.getSwivuUserAction({ previousSelection, clickedItem: record });
      if (!source.multiSelect && this.lastSelection.length) {
        const isClickedAgain = this.lastSelection.find((item) => item.id === record.id);
        if (isClickedAgain && canEmptySwivuList) {
          source.deselect(record);
        } else {
          source.select(record);
        }
      }
      onSwivuListSelectionChange.call(
        this.swivuColumn,
        this.record,
        selected,
        this,
        record,
        action
      );
      if (!canMultiSelectSwivuList) this.hide();
    } else {
      source.deselectAll();
      if (previousSelection) {
        source.select(previousSelection);
      }
    }

    this.lastSelection = ObjectHelper.clone(selected);
  }

  getSwivuUserAction({ previousSelection, clickedItem }) {
    const formatValue = (value) => {
      if (typeof value === "object") return value.id;
      return value;
    };
    if (!previousSelection) return "select";
    const formattedClickedItem = formatValue(clickedItem);
    const isAlreadySelected = Array.isArray(previousSelection)
      ? previousSelection.map(formatValue).includes(formattedClickedItem)
      : formatValue(previousSelection) === formattedClickedItem;
    return isAlreadySelected ? "deselect" : "select";
  }

  swivuRefreshListItems(items) {
    const { tagsList } = this.widgetMap;
    tagsList.items = items || this.swivuColumn.swivuListItems;
  }

  onClickManageBtn(...args) {
    if (this.swivuColumn) {
      this.swivuColumn.onSwivuManageList(...args);
    }
    setTimeout(() => {
      this.close();
    }, 100)
  }

  onSearch({ value }) {
    const { tagsList } = this.widgetMap;
    const swivuSearchField = this.swivuColumn.swivuListSearchKey;
    const store = tagsList.store;
    // Detaching data store from list because assigning filter on the store lags UI
    // Therefore assigning a temp store to list before applying filter to dataStore and reAttaching it after applying filter
    tagsList.store = new Store();
    store.clearFilters();
    if (value.trim()) {
      store.filterBy(record => (record[swivuSearchField] || "").toLowerCase().includes((value.trim() || "").toLowerCase()));
    }
    tagsList.store = store;
  }

  onSearchClear() {
    this.onSearch({ value: "" });
  }

  applyOrderSortToList() {
    const { tagsList } = this.widgetMap;
    tagsList.store.sort({ field: "order" });
  }
}

SwivuEntityTagEditor.initClass();

const SwivuEntityTagEditorPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const { entityRecord, event, ...rest } = record;
  const onShowHandler = useCallback((editor, otherParams = {}) => {
    editor.project = context.project;
    editor.editRecord(entityRecord, context, Object.assign(otherParams, rest));
    editorInstance.editorRef = editor;
  }, [context, record, editorInstance])

  return (
    <SwivuModal
      widgetType={SwivuEntityTagEditor.type}
      isShow={isShow}
      onShow={onShowHandler}
      onHide={onClose}
      displayAt={event.target}
    />
  )
}

export default SwivuEntityTagEditorPopup;
